import type { TableColumn } from '@interstate/components/Table';
import { Tooltip } from '@interstate/components/Tooltip';
import type { ProgramQuote, ProgramsSortSelection } from '@makemydeal/dr-dash-types';
import type { ReactNode } from 'react';

import {
    formatLenderName,
    formatProgramDisplay,
    formatProgramType,
    formatRateProgram,
    formatRateProgramDesc,
    formatRegionalTitle
} from '../../utils/formatLenderProgramName';
import { renderProgramTypeCell } from '../../utils/programTypeCellRenderer';
import { renderLiveAmountCell } from './PaymentCell';

export const LENDER_PAYMENT_DATA_INDEX = 'lenderPayment';

export const leaseColumnsOrder = [
    'selectActionColumn',
    'lenderCode',
    'appliedIncentiveAmount',
    'monthlyPayment',
    'sellRate',
    'buyRate',
    'term',
    'requestedTier',
    'acqFeeFormatted',
    'baseResidualPercent',
    'securityDepositFormated',
    'annualMilesFormatted',
    ['lenderProgram', 'programName'],
    ['lenderProgram', 'rateProgramDesc'],
    'creditTierDescription',
    'frontEndProfit',
    'backEndProfit',
    'actions'
];

export const financeColumnsOrder = [...leaseColumnsOrder.slice(0, 8), ...leaseColumnsOrder.slice(12)];
export const balloonColumnOrder = [
    ...leaseColumnsOrder.slice(0, 8),
    'balloonAmount',
    'balloonTermType',
    ...leaseColumnsOrder.slice(12)
];

const enhancedSharedColumnsOrderStart = [
    'selectActionColumn',
    'lenderCode',
    'monthlyPayment',
    'term',
    'sellRate',
    'buyRate',
    'creditTierDescription',
    'requestedTier',
    ['lenderProgram', 'programName'],
    ['lenderProgram', 'programType'],
    ['lenderProgram', 'rateProgramDesc'],
    ['lenderProgram', 'rateProgram']
];

export const enhancedLeaseColumnsOrder = [
    ...enhancedSharedColumnsOrderStart,
    'baseResidualPercent',
    'annualMilesFormatted',
    'appliedIncentiveAmount',
    'acqFeeFormatted',
    'securityDepositFormated',
    'estReserveGross',
    'reserveSplit',
    'reserveProfit',
    'actions'
];

export const enhancedFinanceColumnsOrder = [
    ...enhancedSharedColumnsOrderStart,
    'appliedIncentiveAmount',
    'estReserveGross',
    'reserveSplit',
    'reserveProfit',
    'actions'
];

export const enhancedBalloonColumnOrder = [
    ...enhancedSharedColumnsOrderStart,
    'balloonAmount',
    'balloonTermType',
    'appliedIncentiveAmount',
    'estReserveGross',
    'reserveSplit',
    'reserveProfit',
    'actions'
];

export const placeRebatesAfterRates = (order: (string | string[])[]): (string | string[])[] => {
    const withoutRebates = order.filter((dataIndex) => dataIndex !== 'appliedIncentiveAmount');
    const lastRateIndex = Math.max(withoutRebates.indexOf('sellRate'), withoutRebates.indexOf('buyRate'));
    if (lastRateIndex < 0 || withoutRebates.length === order.length) return order;
    return [...withoutRebates.slice(0, lastRateIndex + 1), 'appliedIncentiveAmount', ...withoutRebates.slice(lastRateIndex + 1)];
};

export const leaseOnlyColumns = [
    'acqFeeFormatted',
    'baseResidualPercent',
    'baseResidualDollar',
    'securityDepositFormated',
    'annualMilesFormatted'
];

export const getFinanceOnlyColumns = (isLenderProgramNamesRnREnabled: boolean): string[] =>
    isLenderProgramNamesRnREnabled
        ? ['rateType', 'totalProfit', 'baseResidualDollar']
        : ['lenderLegalName', 'rateType', 'totalProfit', 'baseResidualDollar'];

export const getGrossColumns = (isPIScreenEnhancementsEnabled: boolean): string[] =>
    isPIScreenEnhancementsEnabled ? ['frontEndProfit', 'backEndProfit'] : [];

export const getCashExemptColumns = (
    isLenderProgramNamesRnREnabled: boolean,
    isCashFixesEnabled: boolean
): (string | string[])[] => {
    // Balloon Amt / Balloon Term Type only make sense for a balloon/lease-style term structure, and
    // Program Name has no lender program to draw from - cash has neither, so both columns always
    // render blank on the cash program grid. Kept behind the cash-deal-incentives-fixes toggle
    // (rather than made unconditional) so turning that toggle off for any other reason in this story
    // still reverts the cash grid to master's column set exactly.
    const cashOnlyColumns: (string | string[])[] = isCashFixesEnabled
        ? ['balloonAmount', 'balloonTermType', ['lenderProgram', 'programName']]
        : [];
    const piScreenEnhancementsCashExemptColumns = [
        'estReserveGross',
        'reserveSplit',
        'reserveProfit',
        ['lenderProgram', 'rateProgram'],
        ['lenderProgram', 'programType'],
        ['lenderProgram', 'rateProgramDesc']
    ];
    const base = [
        'lenderCode',
        'rateType',
        'buyRate',
        'sellRate',
        'term',
        'monthlyPayment',
        ...cashOnlyColumns,
        ...leaseOnlyColumns,
        ...piScreenEnhancementsCashExemptColumns
    ];
    return isLenderProgramNamesRnREnabled ? base : ['lenderCode', 'lenderLegalName', ...base.slice(1)];
};

const toTitleCase = (value: string): string =>
    value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const renderCreditCell = (value: unknown): ReactNode => (typeof value === 'string' ? toTitleCase(value) : null);

const renderTooltipCell =
    (formatter: (record: ProgramQuote) => string) =>
    (_: unknown, record: ProgramQuote): ReactNode => {
        const text = formatter(record);
        return (
            <Tooltip position="bottom" toolTipContent={text} size="large">
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{text}</span>
            </Tooltip>
        );
    };

const renderPlainCell =
    (formatter: (record: ProgramQuote) => string) =>
    (_: unknown, record: ProgramQuote): ReactNode =>
        formatter(record);

const formatRateProgramCell = renderPlainCell(formatRateProgram);

export const sortByLender = (first: ProgramQuote, second: ProgramQuote): number =>
    (first.lenderCode ?? '').localeCompare(second.lenderCode ?? '');

export const sortByEstReserveGross = (first: ProgramQuote, second: ProgramQuote): number => {
    const a = first.estReserveGross;
    const b = second.estReserveGross;
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    return a - b;
};

const sortByDisplayedText =
    (formatter: (record: ProgramQuote) => string) =>
    (first: ProgramQuote, second: ProgramQuote): number =>
        formatter(first).localeCompare(formatter(second));

export const programColumnSorters = {
    [LENDER_PAYMENT_DATA_INDEX]: sortByLender,
    estReserveGross: sortByEstReserveGross,
    'lenderProgram,programName': sortByDisplayedText(formatProgramDisplay),
    'lenderProgram,programType': sortByDisplayedText(formatProgramType),
    'lenderProgram,rateProgramDesc': sortByDisplayedText(formatRateProgramDesc),
    'lenderProgram,rateProgram': sortByDisplayedText(formatRateProgram)
};

export const withRestoredSortOrder = (columns: TableColumn[], sort?: ProgramsSortSelection | null): TableColumn[] =>
    sort
        ? columns.map((column) => (String(column.dataIndex) === sort.column ? { ...column, defaultSortOrder: sort.order } : column))
        : columns;

const MERGED_CELL_LINE_STYLE = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block'
} as const;

export type LivePaymentOverlay = { appliedKey: string; amount?: number; baseline?: number };

const createLenderPaymentColumn = (livePayment?: LivePaymentOverlay): TableColumn => ({
    dataIndex: LENDER_PAYMENT_DATA_INDEX,
    title: 'Lender/ Payment',
    width: '9rem',
    render: (_value: unknown, record: ProgramQuote & { key?: string }) => (
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, overflow: 'hidden' }}>
            <Tooltip position="bottom" toolTipContent={formatLenderName(record)} size="large">
                <span style={MERGED_CELL_LINE_STYLE}>{record.lenderCode}</span>
            </Tooltip>
            <span style={MERGED_CELL_LINE_STYLE}>
                {renderLiveAmountCell(
                    record.monthlyPayment,
                    record,
                    livePayment?.appliedKey ?? '',
                    livePayment?.amount,
                    livePayment?.baseline,
                    'Payment'
                )}
            </span>
        </span>
    )
});

export const toCompactColumnsOrder = (order: (string | string[])[]): (string | string[])[] =>
    order
        .filter((dataIndex) => dataIndex !== 'monthlyPayment')
        .map((dataIndex) => (dataIndex === 'lenderCode' ? LENDER_PAYMENT_DATA_INDEX : dataIndex));

export const getColumnsCompactUI = (
    isLenderProgramNamesRnREnabled: boolean,
    isPIScreenEnhancementsEnabled: boolean,
    isCompactFrozenLayout = false,
    livePayment?: LivePaymentOverlay
): TableColumn[] => {
    const selectAction: TableColumn = { dataIndex: 'selectActionColumn', title: '', defaultSortOrder: 'ascend', width: '6.5rem' };
    const lenderColumns: TableColumn[] = isLenderProgramNamesRnREnabled
        ? [
              { dataIndex: 'lenderCode', title: 'Lender', width: '12rem', render: renderTooltipCell(formatLenderName) },
              {
                  dataIndex: ['lenderProgram', 'programName'],
                  title: 'Program Name',
                  width: '7rem',
                  render: renderTooltipCell(formatProgramDisplay)
              }
          ]
        : [
              { dataIndex: 'lenderCode', title: 'Lender', width: '6rem' },
              { dataIndex: 'lenderLegalName', title: 'Lender Name' },
              { dataIndex: ['lenderProgram', 'programName'], title: 'Program Name', width: '7rem' }
          ];

    const leadingColumns: TableColumn[] = isCompactFrozenLayout
        ? [createLenderPaymentColumn(livePayment), ...lenderColumns.filter((column) => column.dataIndex !== 'lenderCode')]
        : lenderColumns;

    return [
        selectAction,
        ...leadingColumns,
        ...(isPIScreenEnhancementsEnabled
            ? [
                  {
                      dataIndex: ['lenderProgram', 'programType'],
                      title: 'Program Type',
                      width: '8rem',
                      render: renderProgramTypeCell(formatProgramType, formatRegionalTitle)
                  },
                  {
                      dataIndex: ['lenderProgram', 'rateProgramDesc'],
                      title: 'Rate Program Desc',
                      width: '10rem',
                      render: renderTooltipCell(formatRateProgramDesc)
                  },
                  {
                      dataIndex: ['lenderProgram', 'rateProgram'],
                      title: 'Rate Program',
                      width: '8rem',
                      render: formatRateProgramCell
                  },
                  { dataIndex: 'estReserveGross', title: 'Est. Reserve Gross', width: '9rem' },
                  { dataIndex: 'reserveSplit', title: 'Reserve Split', width: '8rem' },
                  { dataIndex: 'reserveProfit', title: 'Reserve Profit', width: '8rem' }
              ]
            : []),
        { dataIndex: 'creditTierDescription', title: 'Tier', width: '6rem' },
        { dataIndex: 'appliedIncentiveAmount', title: 'Rebates', width: '8.5rem' },
        { dataIndex: 'sellRate', title: isPIScreenEnhancementsEnabled ? 'Sell Rate' : 'Rate/MF', width: '7rem' },
        { dataIndex: 'rateType', title: 'Rate Type' },
        { dataIndex: 'buyRate', title: 'Buy Rate', width: '6rem' },
        { dataIndex: 'term', title: 'Term', width: '5.5rem' },
        { dataIndex: 'requestedTier', title: 'Credit Score', width: '7rem', render: renderCreditCell },
        { dataIndex: 'acqFeeFormatted', title: 'Acq. Fee', width: '6rem' },
        { dataIndex: 'baseResidualPercent', title: 'Residual %', width: '7rem' },
        { dataIndex: 'baseResidualDollar', title: 'Residual Amt' },
        { dataIndex: 'balloonAmount', title: 'Balloon Amt', width: '8rem' },
        { dataIndex: 'balloonTermType', title: 'Balloon Term Type', width: '8rem' },
        { dataIndex: 'securityDepositFormated', title: 'Security Deposit', width: '8rem' },
        { dataIndex: 'annualMilesFormatted', title: 'Miles / Year', width: '7rem' },
        ...(isCompactFrozenLayout ? [] : [{ dataIndex: 'monthlyPayment', title: 'Payment', width: '7rem' }]),
        { dataIndex: 'frontEndProfit', title: 'Front Gross', width: '8rem' },
        { dataIndex: 'backEndProfit', title: 'Back Gross', width: '8rem' },
        { dataIndex: 'totalProfit', title: 'Total Gross', width: '6rem' },
        { dataIndex: 'actions', fixed: 'right', title: '', width: '0.1rem' }
    ];
};

const formatCashType = (value?: string): string =>
    (value ?? '')
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();

const renderNoWrap = (value?: string): ReactNode => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>;

const renderCashType = (value?: string): ReactNode => renderNoWrap(formatCashType(value));

export const nestedColumnsCompactUI: TableColumn[] = [
    { dataIndex: 'included', title: '', width: '80px' },
    { dataIndex: 'programId', title: 'Programs #', width: '110px' },
    { dataIndex: 'programName', title: 'Incentives Name', width: '28%' },
    { dataIndex: 'incentiveId', title: 'Code', width: '90px' },
    { dataIndex: 'displayType', title: 'Type', width: '110px', render: renderNoWrap },
    { dataIndex: 'incentiveType', title: 'Cash Type', width: '130px', render: renderCashType },
    { dataIndex: 'cash', title: 'Amount', width: '90px' },
    { dataIndex: 'actions-icon', title: '', width: '110px' }
];

export const createColumnsWithSorter = (
    renderFunction: (text: unknown, record: ProgramQuote, index: number) => ReactNode,
    appliedProgramKey: string | null,
    isLenderProgramNamesRnREnabled: boolean,
    isPIScreenEnhancementsEnabled: boolean,
    isCompactFrozenLayout = false,
    livePayment?: LivePaymentOverlay
): TableColumn[] => {
    const columns = [
        ...getColumnsCompactUI(isLenderProgramNamesRnREnabled, isPIScreenEnhancementsEnabled, isCompactFrozenLayout, livePayment)
    ];

    columns[0] = {
        ...columns[0],
        render: renderFunction,
        sorter: (a: ProgramQuote & { key: string }, b: ProgramQuote & { key: string }) => {
            if (a.key === appliedProgramKey) return -1;
            if (b.key === appliedProgramKey) return 1;
            return 0;
        }
    };

    return columns;
};
